import type { IsoDate } from './dates';
import { isRecipeAllowed } from './diet';
import type { Member } from './household';
import { toCents, toEuros } from './money';
import { removeFromPantry, type Pantry } from './pantry';
import { bestPrice, productOf, type PriceBook } from './price-book';
import { isQuick, type Recipe } from './recipe';
import { packagesNeeded } from './shopping';
import type { StoreId } from './types';

export interface MissingItem {
  productId: string;
  quantity: number;
  packages: number;
  cost: number;
}

export interface CookMatch {
  recipeId: string;
  matchPercent: number;
  missing: MissingItem[];
  missingCost: number;
}

export interface CookSearchContext {
  recipes: readonly Recipe[];
  members: readonly Member[];
  book: PriceBook;
  date: IsoDate;
  storeIds: readonly StoreId[];
  quick: { activeMinutes: number; totalMinutes: number };
  staples: readonly string[];
}

export interface CookSearchOptions {
  maxMissing?: number;
  quickOnly?: boolean;
}

function missingItem(productId: string, quantity: number, context: CookSearchContext): MissingItem {
  const product = productOf(context.book.catalog, productId);
  const packages = packagesNeeded(quantity, product.packSize);
  const price = bestPrice(context.book, productId, context.storeIds, context.date);
  return { productId, quantity, packages, cost: toEuros(price.priceCents * packages) };
}

function matchRecipe(pantry: Pantry, recipe: Recipe, context: CookSearchContext): CookMatch {
  const needed = recipe.ingredients.filter((i) => !context.staples.includes(i.productId));
  const coverage = needed.map((i) => Math.min(1, (pantry[i.productId] ?? 0) / i.quantity));
  const share = coverage.length === 0 ? 1 : coverage.reduce((a, b) => a + b, 0) / coverage.length;
  const missing = needed
    .filter((i) => (pantry[i.productId] ?? 0) < i.quantity)
    .map((i) => missingItem(i.productId, i.quantity - (pantry[i.productId] ?? 0), context));
  const missingCents = missing.reduce((sum, item) => sum + toCents(item.cost), 0);
  return {
    recipeId: recipe.id,
    matchPercent: Math.round(share * 100),
    missing,
    missingCost: toEuros(missingCents),
  };
}

/**
 * DE: Rezepte nach Vorrat sortieren: wie viel ist da, was fehlt und was kostet der Rest.
 * Grundzutaten zählen nie als fehlend.
 * EN: Rank recipes by the pantry: how much is there, what is missing and what the rest costs.
 * Staples never count as missing.
 */
export function findCookable(
  pantry: Pantry,
  context: CookSearchContext,
  options: CookSearchOptions = {},
): CookMatch[] {
  return context.recipes
    .filter((recipe) => isRecipeAllowed(recipe, context.members, context.book.catalog))
    .filter((recipe) => !options.quickOnly || isQuick(recipe, context.quick))
    .map((recipe) => ({ recipe, match: matchRecipe(pantry, recipe, context) }))
    .filter(
      ({ match }) => options.maxMissing === undefined || match.missing.length <= options.maxMissing,
    )
    .sort(
      (a, b) =>
        b.match.matchPercent - a.match.matchPercent ||
        a.match.missingCost - b.match.missingCost ||
        a.recipe.name.localeCompare(b.recipe.name, 'uk'),
    )
    .map(({ match }) => match);
}

/**
 * DE: Nach dem Kochen die verbrauchten Mengen aus dem Vorrat nehmen.
 * EN: After cooking, take the used quantities out of the pantry.
 */
export function cookRecipe(pantry: Pantry, recipe: Recipe, servings: number): Pantry {
  const factor = servings / recipe.baseServings;
  return recipe.ingredients.reduce(
    (current, ingredient) =>
      removeFromPantry(current, ingredient.productId, ingredient.quantity * factor),
    pantry,
  );
}
